/**
 * Learner course features gated by the seat license consumed for this enrollment
 * (UserCourses.licenseBatch), not the org's current portal license.
 */

import {
  organizationSeatLicenseType,
  resolveSeatLicenseType,
} from '@/components/refRepsPortal/portalOrganization.js'

/** Seat license types that include knowledge checks on assigned courses (learner enrollment). */
export const SEAT_LICENSE_TYPES_WITH_KNOWLEDGE_CHECKS = Object.freeze([
  'Premium',
  'Supp. Premium',
])

/** Org license types that show knowledge checks in instructor course/module UI. */
export const ORG_LICENSE_TYPES_WITH_KNOWLEDGE_CHECKS = Object.freeze(['Premium'])

/**
 * @param {Record<string, unknown> | null | undefined} org
 */
export function organizationHasKnowledgeChecks(org) {
  const orgType = resolveSeatLicenseType(organizationSeatLicenseType(org))
  return ORG_LICENSE_TYPES_WITH_KNOWLEDGE_CHECKS.includes(orgType)
}

/**
 * @param {Record<string, unknown> | null | undefined} org
 * @returns {{ includeKnowledgeChecks: boolean }}
 */
export function buildInstructorModuleSidebarOptions(org) {
  return {
    includeKnowledgeChecks: organizationHasKnowledgeChecks(org),
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} userCourse
 * @returns {string | null}
 */
export function seatLicenseTypeFromUserCourse(userCourse) {
  if (!userCourse || typeof userCourse !== 'object') {
    return null
  }
  const batch = userCourse.licenseBatch ?? userCourse.license_batch
  if (batch && typeof batch === 'object') {
    const fromBatch = batch.licenseType ?? batch.type ?? batch.license_type
    if (fromBatch) {
      return String(fromBatch)
    }
  }
  const direct = userCourse.seatLicenseType ?? userCourse.seat_license_type
  return direct != null ? String(direct) : null
}

/**
 * @param {Record<string, unknown> | null | undefined} userCourse
 */
export function userCourseHasKnowledgeChecks(userCourse) {
  const licenseType = seatLicenseTypeFromUserCourse(userCourse)
  return licenseTypeIncludesKnowledgeChecks(licenseType)
}

/**
 * @param {string | null | undefined} licenseType
 */
export function licenseTypeIncludesKnowledgeChecks(licenseType) {
  if (!licenseType) {
    return false
  }
  const raw = String(licenseType)
  if (SEAT_LICENSE_TYPES_WITH_KNOWLEDGE_CHECKS.includes(raw)) {
    return true
  }
  const resolved = resolveSeatLicenseType(raw)
  return SEAT_LICENSE_TYPES_WITH_KNOWLEDGE_CHECKS.includes(resolved)
}

/** @param {Record<string, unknown> | null | undefined} userCourse */
export function licenseBatchNeedsProgressUpdate(userCourse) {
  const batch = userCourse?.licenseBatch ?? userCourse?.license_batch
  return batch?.needsProgressUpdate === true
}

/**
 * @param {Record<string, unknown> | null | undefined} userCourse
 * @returns {{ includeKnowledgeChecks: boolean }}
 */
export function buildModuleSidebarOptions(userCourse) {
  return {
    includeKnowledgeChecks: userCourseHasKnowledgeChecks(userCourse),
  }
}

/**
 * Course modules shaped for learner progress / sidebar (strips KC when not entitled).
 * @param {unknown} modules
 * @param {Record<string, unknown> | null | undefined} userCourse
 */
export function modulesForLearnerEnrollment(modules, userCourse) {
  if (!Array.isArray(modules)) {
    return []
  }
  if (userCourseHasKnowledgeChecks(userCourse)) {
    return modules
  }
  return modules.map((m) => {
    if (!m || typeof m !== 'object') {
      return m
    }
    return { ...m, knowledge_checks: [] }
  })
}

/**
 * Resolved seat license on a learner enrollment (UserCourses row or roster summary).
 * @param {Record<string, unknown> | null | undefined} userCourse
 * @returns {string | null}
 */
export function resolvedSeatLicenseTypeFromEnrollment (userCourse) {
  return seatLicenseTypeFromUserCourse(userCourse)
}

/**
 * @param {Record<string, unknown> | null | undefined} userCourse
 * @param {Record<string, unknown> | null | undefined} org
 */
export function learnerEnrollmentLicenseMatchesOrganization(userCourse, org) {
  const learnerType = resolvedSeatLicenseTypeFromEnrollment(userCourse)
  const orgType = resolveSeatLicenseType(organizationSeatLicenseType(org))
  if (!learnerType) {
    return false
  }
  return learnerType === orgType
}

/**
 * @param {Record<string, unknown> | null | undefined} userCourse
 * @param {Record<string, unknown> | null | undefined} org
 */
export function learnerEnrollmentLicenseMismatchMessage(userCourse, org) {
  const orgType = resolveSeatLicenseType(organizationSeatLicenseType(org))
  const learnerType = resolvedSeatLicenseTypeFromEnrollment(userCourse)
  if (!learnerType) {
    return `This learner does not have a seat license recorded for this course. Your organization is on ${orgType}. Reassign the course under your org's current license before they can join group watch.`
  }
  return `This learner's course seat license (${learnerType}) does not match your organization's current license (${orgType}). Reassign the course under your org's current license before they can join group watch.`
}
